import { useState, useEffect, useRef, useCallback } from 'react';
import { FileStat } from '../api/IFileSystem';
import { useFileManagerStore } from '../store/useFileManagerStore';

export interface DriveSize {
  total_size: number;
  free_size: number;
  used_size: number;
}

interface DriveSizeCache {
  [volume: string]: {
    result: DriveSize;
    timestamp: number;
  };
}

// Type guard for WebRTCSystem-like object
interface DriveSizeCapable {
  getVolumeSize: (volume: string) => Promise<DriveSize | null>;
}

function hasDriveSizeMethods(fs: any): fs is DriveSizeCapable {
  return fs && typeof fs.getVolumeSize === 'function';
}

const CACHE_TTL = 60000; // 1 minute cache for drive sizes

/**
 * Hook for fetching drive sizes for "This PC" view
 */
export function useDriveSize(currentPath: string, files: FileStat[]) {
  const [sizes, setSizes] = useState<Record<string, DriveSize>>({});
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<DriveSizeCache>({});
  const { fs } = useFileManagerStore();

  // Fetch drive sizes when on root path
  const fetchDriveSizes = useCallback(async (drives: FileStat[]) => {
    if (!hasDriveSizeMethods(fs)) return;
    if (drives.length === 0) return;

    // Filter to only drives not already cached
    const drivesToFetch = drives.filter(d => {
      const volume = d.path.slice(0, 2);
      const cached = cacheRef.current[volume];
      return !cached || Date.now() - cached.timestamp >= CACHE_TTL;
    });

    // Apply cached values first
    const cached: Record<string, DriveSize> = {};
    drives.forEach(d => {
      const volume = d.path.slice(0, 2);
      if (cacheRef.current[volume]) {
        cached[volume] = cacheRef.current[volume].result;
      }
    });
    if (Object.keys(cached).length > 0) {
      setSizes(prev => ({ ...prev, ...cached }));
    }

    if (drivesToFetch.length === 0) return;

    setLoading(true);
    console.log('[useDriveSize] Fetching sizes for', drivesToFetch.length, 'drives');

    // Fetch all drive sizes in parallel
    const results = await Promise.all(
      drivesToFetch.map(async (drive) => {
        const volume = drive.path.slice(0, 2);
        try {
          const result = await fs.getVolumeSize(volume);
          if (result) {
            cacheRef.current[volume] = { result, timestamp: Date.now() };
            return { volume, result };
          }
        } catch (e) {
          console.error('[useDriveSize] Failed to get size for', volume, e);
        }
        return null;
      })
    );

    // Update state with results
    const newSizes: Record<string, DriveSize> = {};
    results.forEach(r => {
      if (r) {
        newSizes[r.volume] = r.result;
      }
    });

    if (Object.keys(newSizes).length > 0) {
      setSizes(prev => ({ ...prev, ...newSizes }));
    }

    setLoading(false);
  }, [fs]);

  // Auto-fetch when on root path and files change
  useEffect(() => {
    if (currentPath !== 'root') {
      setSizes({});
      return;
    }

    const drives = files.filter(f => f.type === 'drive');
    if (drives.length > 0) {
      fetchDriveSizes(drives);
    }
  }, [currentPath, files, fetchDriveSizes]);

  return {
    sizes,
    loading,
    fetchDriveSizes,
  };
}
